import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import AppLoader from 'core/ui/components/AppLoader';

import { storeCurrenciesAction } from '../actions/filterActions';
import { setSalesTotalAction, setLoadingAction, setComparisonSalesTotalAction } from '../actions/graphActions';
import { useReportableDataDispatch, useReportableDataState } from '../context/UseReportableDataContext';
import * as ReportableDataApi from '../data/ReportableDataApi';
import { ReportableDashboardFilters } from './ReportableDashboardFilters';
import NorthStarPanel from './stats/NorthStarPanel';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const NorthStarPanelWrapper = styled.div`
  grid-area: NorthStarPanel;
`;

export const ReportableDashboard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { siteId } = useParams<{ siteId: string }>();
  const dispatch = useReportableDataDispatch();
  const {
    queryParameters: {
      filterCurrency,
      comparisonType,
      dateRange,
      dateRangeOffset,
      comparisonDateRange,
      comparisonDateRangeOffset,
    },
    graphParameters: { granularity, baseCurrency, comparisonEnabled, timezone },
    loading,
    dateTarget,
    comparisonDateTarget,
    queryString,
  } = useReportableDataState();
  const { isFetching: isFetchingCurrencies, data: currenciesResponse } = ReportableDataApi.useGetCurrenciesQuery(
    siteId,
  );
  const { isFetching: isSalesTotalsFetching, data: salesTotalsResponse } = ReportableDataApi.useGetSalesTotalsQuery(
    siteId,
    dateTarget,
    queryString,
  );
  const {
    isFetching: isComparisonSalesTotalsFetching,
    data: comparisonSalesTotalsResponse,
  } = ReportableDataApi.useGetSalesTotalsQuery(siteId, comparisonDateTarget, queryString);

  useEffect(() => {
    const loadingData = isFetchingCurrencies && isSalesTotalsFetching;
    setLoadingAction(dispatch, loadingData);
  }, [isFetchingCurrencies, isSalesTotalsFetching, dispatch]);

  useEffect(() => {
    if (!isFetchingCurrencies && currenciesResponse) {
      const currencies: string[] = currenciesResponse.data;
      storeCurrenciesAction(dispatch, currencies);
    }
  }, [isFetchingCurrencies, loading, currenciesResponse, dispatch]);

  useEffect(() => {
    if (!isSalesTotalsFetching && salesTotalsResponse) {
      const salesTotals = salesTotalsResponse.data;
      setSalesTotalAction(dispatch, salesTotals);
    }
  }, [isSalesTotalsFetching, salesTotalsResponse, dispatch]);

  useEffect(() => {
    if (!isComparisonSalesTotalsFetching && comparisonSalesTotalsResponse) {
      const comparisonSalesTotals = comparisonSalesTotalsResponse.data;
      setComparisonSalesTotalAction(dispatch, comparisonSalesTotals);
    }
  }, [comparisonEnabled, isComparisonSalesTotalsFetching, comparisonSalesTotalsResponse, dispatch]);

  if (loading) {
    return <AppLoader />;
  }

  return (
    <>
      <ReportableDashboardFilters />
      <NorthStarPanelWrapper>
        <NorthStarPanel />
      </NorthStarPanelWrapper>
      <h2>{t('dateString')}</h2>
      <p>{dateTarget}</p>
      <h2>{t('dateString')}</h2>
      <p>{comparisonDateTarget}</p>
      <h2>{t('queryString')}</h2>
      <p>{queryString}</p>
      <h2>{t('graphParameters.heading')}</h2>
      <table>
        <thead>
          <tr>
            <th>{t('graphParameters.table.field')}</th>
            <th>{t('graphParameters.table.value')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{t('graphParameters.table.granularity')}:</td>
            <td>{granularity}</td>
          </tr>
          <tr>
            <td>{t('graphParameters.table.granularity')}:</td>
            <td>{baseCurrency}</td>
          </tr>
          <tr>
            <td>{t('graphParameters.table.granularity')}:</td>
            <td>{String(comparisonEnabled)}</td>
          </tr>
        </tbody>
      </table>

      <h2>{t('queryParameters.heading')}</h2>
      <table>
        <thead>
          <tr>
            <th>{t('queryParameters.table.field')}</th>
            <th>{t('queryParameters.table.value')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{t('queryParameters.table.timezone')}:</td>
            <td>{timezone}</td>
          </tr>
          <tr>
            <td>{t('queryParameters.table.filterCurrency')}:</td>
            <td>{filterCurrency}</td>
          </tr>
          <tr>
            <td>{t('queryParameters.table.dateRange')}:</td>
            <td>{dateRange}</td>
          </tr>
          <tr>
            <td>{t('queryParameters.table.dateRangeOffset')}:</td>
            <td>{dateRangeOffset}</td>
          </tr>
          <tr>
            <td>{t('queryParameters.table.comparisonType')}:</td>
            <td>{comparisonType}</td>
          </tr>
          <tr>
            <td>{t('queryParameters.table.comparisonDateRange')}:</td>
            <td>{comparisonDateRange}</td>
          </tr>
          <tr>
            <td>{t('queryParameters.table.comparisonDateRangeOffset')}:</td>
            <td>{comparisonDateRangeOffset}</td>
          </tr>
        </tbody>
      </table>
    </>
  );
};
